import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RecommendationDto } from "@green0meter/api-client";
import { isEmpty } from "lodash";

import { Recommendations } from "./components/Recommendations";
import Tabs from "./components/Tabs";
import Filters from "./components/Filters";
import RecommendationsListSkeleton from "./RecommendationsList.skeleton";

import { useRecommendationsApi } from "@api";
import { useRecommendationsStore } from "@store/recommendations";
import { RecommendationType } from "@type/Recommendation";
import { Card, CardContent, Stack, Typography } from "@components/base";
import { useUserStore } from "@store/user";

interface Component {}

const Component: FC<Component> = () => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { isLoadingRecommendations, recommendations, recommendationsEnabled } =
    useRecommendationsApi(user);
  const { filter, activeTab } = useRecommendationsStore();
  const [filteredRecommendations, setFilteredRecommendations] = useState<
    RecommendationDto[]
  >([]);

  useEffect(() => {
    if (!isLoadingRecommendations && recommendations) {
      setFilteredRecommendations(
        recommendations.filter((recommendation) => {
          let matchingScope = true;

          if (!isEmpty(filter.scopes)) {
            matchingScope = false;
            filter.scopes.forEach((scope) => {
              if (recommendation.scope.includes(scope.toString())) {
                matchingScope = true;
              }
            });
          }

          let matchingType = true;

          if (!isEmpty(filter.types)) {
            matchingType = false;
            if (filter.types.includes(recommendation.type as RecommendationType)) {
              matchingType = true;
            }
          }

          let matchingState = true;

          if (!isEmpty(filter.states)) {
            filter.states.forEach((state) => {
              matchingState = false;
              if (state === "completed") {
                matchingState = recommendation.isCompleted;
              }
              if (state === "not-relevant") {
                matchingState = recommendation.isNotRelevant;
              }
            });
          }

          let matchingTab = false;

          if (!activeTab || activeTab === "co2") {
            matchingTab =
              !recommendation.isCompleted && !recommendation.isNotRelevant;
          } else if (activeTab === "archive") {
            matchingTab = recommendation.isCompleted || recommendation.isNotRelevant;
          }

          return matchingScope && matchingType && matchingTab && matchingState;
        }),
      );
    }
  }, [filter, recommendations]);

  if (isLoadingRecommendations) {
    return <RecommendationsListSkeleton />;
  }

  return (
    recommendationsEnabled && (
      <Card id="co2-recommendations-list">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={3}
          pb={0}
        >
          <Typography variant="subtitle1">
            {t("recommendationsList.title")}
          </Typography>
        </Stack>
        <CardContent>
          {recommendations ? (
            <Stack spacing={2}>
              <Tabs />
              <Filters />
              <Recommendations recommendations={filteredRecommendations} />
            </Stack>
          ) : (
            <Typography>
              {t("recommendationsList.emptyMessage.line1")}
              <br />
              {t("recommendationsList.emptyMessage.line2")}
            </Typography>
          )}
        </CardContent>
      </Card>
    )
  );
};

export default Component;
